import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  CircularProgress,
  Alert,
  Card,
} from '@mui/material';
import {
  Image as ImageIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateRight as RotateRightIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import { fieldInspectionService } from '../../services/fieldInspectionService';

const ImageViewer = ({
  imagePath = null,
  imageUrl = null,
  
  alt = 'Image',
  title = 'Image',
  description = '',
  downloadFilename = 'image.jpg',
  thumbnailWidth = '200px',
  thumbnailHeight = '120px',
  
  borderRadius = 2,
  showHoverEffects = true,
  showDescriptionOverlay = true,
  className = '',
}) => {
  const theme = useTheme();
  const [loadedImageUrl, setLoadedImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [forceReload, setForceReload] = useState(0);

  useEffect(() => {
    const loadImage = async () => {
      setImageError(false);
      
      if (imageUrl) {
        setLoadedImageUrl(imageUrl);
        setImageLoading(false);
        return;
      }
      
      if (imagePath) {
        setImageLoading(true);
        try {
          const imageBlob = await fieldInspectionService.getUploadedImage(imagePath);
          const objectUrl = URL.createObjectURL(imageBlob);
          setLoadedImageUrl(objectUrl);
        } catch (error) {
          console.error('Error loading image:', error);
          setLoadedImageUrl(null);
          setImageError(true);
        } finally {
          setImageLoading(false);
        }
      } else {
        setLoadedImageUrl(null);
        setImageLoading(false);
      }
    };

    loadImage();
  }, [imagePath, imageUrl, forceReload]);

  useEffect(() => {
    return () => {
      if (loadedImageUrl && imagePath) {
        URL.revokeObjectURL(loadedImageUrl);
      }
    };
  }, [loadedImageUrl, imagePath]);

  if (imageLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
        <CircularProgress size={20} sx={{ mr: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Loading image...
        </Typography>
      </Box>
    );
  }

  if (imageError || (!imageLoading && !loadedImageUrl && (imagePath || imageUrl))) {
    return (
      <Card sx={{ my: 1, textAlign: 'center', borderColor: theme.palette.error.main, borderWidth: 1, borderStyle: 'solid' }}>
        <Button
          variant="text"
          startIcon={<RefreshIcon />}
          onClick={() => setForceReload((prev) => prev + 1)}
        >
          Retry
        </Button>
      </Card>
    );
  }

  if (!loadedImageUrl && !imagePath && !imageUrl) {
    return (
      <Typography variant="body2" color="text.secondary">
        No image available
      </Typography>
    );
  }

  if (loadedImageUrl) {
    return (
      <Box className={className}>
        <PhotoProvider
          maskOpacity={0.9}
          speed={() => 300}
          easing={() => 'cubic-bezier(0.25, 0.8, 0.25, 1)'}
          maskClosable={true}
          bannerVisible={true}
          toolbarRender={({ onScale, scale, rotate, onRotate }) => (
            <Box
              className="custom-photo-toolbar"
              sx={{
                position: 'absolute',
                top: 2,
                right: 50,
                display: 'flex',
                gap: 0.5,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                borderRadius: 2,
                p: 1,
                zIndex: 10000,
                backdropFilter: 'blur(4px)'
              }}
            >
              <Tooltip title="Zoom In">
                <IconButton
                  onClick={() => onScale(scale + 0.3)}
                  size="small"
                  sx={{ 
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zoom Out">
                <IconButton
                  onClick={() => onScale(scale - 0.3)}
                  size="small"
                  sx={{ 
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rotate">
                <IconButton
                  onClick={() => onRotate(rotate + 90)}
                  size="small"
                  sx={{ 
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  <RotateRightIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download">
                <IconButton
                  onClick={() => {
                    if (loadedImageUrl) {
                      const link = document.createElement('a');
                      link.href = loadedImageUrl;
                      link.download = downloadFilename;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }
                  }}
                  size="small"
                  sx={{ 
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          overlayRender={() => showDescriptionOverlay && (title || description) && (
            <Box
              className="image-description-overlay"
              sx={{
                position: 'absolute',
                bottom: 20,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                color: 'white',
                px: 3,
                py: 2,
                borderRadius: 2,
                textAlign: 'center',
                maxWidth: '90%',
                zIndex: 10000,
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(255,255,255,0.1)'
              }}
            >
              {title && (
                <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                  {title}
                </Typography>
              )}
              {description && (
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {description}
                </Typography>
              )}
            </Box>
          )}
          className="custom-photo-provider"
        >
          <PhotoView src={loadedImageUrl}>
            <Box
              className="photo-thumbnail"
              sx={{
                position: 'relative',
                cursor: 'pointer',
                borderRadius: borderRadius,
                overflow: 'hidden',
                border: '2px solid transparent',
                transition: showHoverEffects ? 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
                boxShadow: 1,
                display: 'inline-block',
                maxWidth: thumbnailWidth,
                width: thumbnailWidth === '100%' ? '100%' : thumbnailWidth,
                ...(showHoverEffects && {
                  '&:hover': {
                    border: '2px solid',
                    borderColor: 'primary.main',
                    transform: 'scale(1.03)',
                    boxShadow: 6,
                    '& .photo-overlay': {
                      opacity: 1
                    },
                    '& .photo-zoom-icon': {
                      transform: 'scale(1.2)'
                    }
                  }
                })
              }}
            >
              <img 
                src={loadedImageUrl}
                alt={alt}
                style={{
                  width: '100%',
                  height: thumbnailHeight,
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
              
              {/* Hover Overlay */}
              {showHoverEffects && (
                <Box
                  className="photo-overlay"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.3s ease'
                  }}
                >
                  <Box
                    className="photo-zoom-icon"
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      borderRadius: '50%',
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    <FullscreenIcon color="primary" />
                  </Box>
                </Box>
              )}

              {/* Bottom Description */}
              {showDescriptionOverlay && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                    color: 'white',
                    p: 1,
                    minHeight: 30
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                    Click to view full size
                  </Typography>
                </Box>
              )}
            </Box>
          </PhotoView>
        </PhotoProvider>
      </Box>
    );
  }

  return null;
};

export default ImageViewer;
